/* ProjectDashboard — Step 9 Feature 6 single-page health overview.
 *
 * Reads /api/projects/{id}/dashboard and renders:
 *   • Top bar: project name, status badges (compliance, stages done)
 *   • KPI cards row: BUA, FSI actual/limit, Coverage actual/limit
 *   • Stage completion grid: A → L tiles (Ran / Pending / entity count)
 *   • Cost summary card with section breakdown bars
 *   • Risk / issues list (sorted by severity)
 *   • Quick links to procurement-pack + master-plan + engineering
 *
 * No editing — pure read-only.
 */
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, CheckCircle2, XCircle, AlertCircle, Loader2,
  Building2, Layers, Activity, FileSpreadsheet, Map, Cog,
  Download, GitCompare, FileCheck2,
} from "lucide-react";
import RevisionPanel from "../components/revisions/RevisionPanel";

const API = process.env.REACT_APP_BACKEND_URL;

const SEV_COLOR = {
  high: { bg: "#7f1d1d22", border: "#dc2626", color: "#fca5a5" },
  medium: { bg: "#78350f22", border: "#f59e0b", color: "#fcd34d" },
  low: { bg: "#1e3a8a22", border: "#3b82f6", color: "#93c5fd" },
};

function Card({ children, ...rest }) {
  return (
    <div
      style={{
        background: "#0b1220", border: "1px solid #1e293b",
        borderRadius: 8, padding: 18,
      }}
      {...rest}>
      {children}
    </div>
  );
}

function KpiTile({ label, value, sub, status, testid }) {
  const sc = status === "PASS" ? "#10b981"
            : status === "FAIL" ? "#ef4444" : "#94a3b8";
  return (
    <Card data-testid={testid} style={{ flex: 1, minWidth: 180 }}>
      <div style={{
        fontSize: 11, color: "#94a3b8", textTransform: "uppercase",
        letterSpacing: 0.6, fontWeight: 600,
      }}>{label}</div>
      <div style={{
        fontSize: 28, fontWeight: 700, color: "#e2e8f0",
        marginTop: 8,
      }}>{value}</div>
      {sub && (
        <div style={{
          fontSize: 11, color: sc, marginTop: 4, fontWeight: 600,
        }}>{sub}</div>
      )}
    </Card>
  );
}

function StageTile({ row }) {
  const isOK = row.ran;
  return (
    <div
      data-testid={`dashboard-stage-${row.stage}`}
      style={{
        padding: 10,
        border: `1px solid ${isOK ? "#15803d" : "#334155"}`,
        background: isOK ? "#052e1644" : "#0f172a",
        borderRadius: 6, display: "flex", flexDirection: "column",
        gap: 4,
      }}>
      <div style={{
        display: "flex", alignItems: "center",
        justifyContent: "space-between",
      }}>
        <span style={{ fontWeight: 700, fontSize: 14,
                          color: "#e2e8f0" }}>
          Stage {row.stage}
        </span>
        {isOK ? (
          <CheckCircle2 size={14} style={{ color: "#10b981" }} />
        ) : (
          <AlertCircle size={14} style={{ color: "#94a3b8" }} />
        )}
      </div>
      <div style={{ fontSize: 10, color: "#94a3b8" }}>
        {row.entities_covered > 0
          ? `${row.entities_covered} entities`
          : isOK ? "simulated" : "pending"}
      </div>
      <div style={{ fontSize: 10, color: "#64748b" }}>
        {row.deliverables_expected} deliverables
      </div>
    </div>
  );
}

export default function ProjectDashboard() {
  const { id: projectId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [projMeta, setProjMeta] = useState(null);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    setError(null);
    axios.get(`${API}/api/projects/${projectId}/dashboard`,
                { withCredentials: true })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((e) => {
        if (!cancelled) setError(
          e?.response?.data?.detail || e.message);
      });
    // Companion fetch for canonical `domain` (RG9 · Wave-4).
    // Dashboard payload has `industry` (user-facing) which is `generic`
    // for legacy projects. `domain` is the canonical backend flag.
    axios.get(`${API}/api/projects/${projectId}`,
                { withCredentials: true })
      .then((res) => { if (!cancelled) setProjMeta(res.data); })
      .catch(() => { /* silent — button just won't render */ });
    return () => { cancelled = true; };
  }, [projectId]);

  if (error) {
    return (
      <div style={{ padding: 24, color: "#fca5a5" }}
          data-testid="dashboard-error">
        Failed to load dashboard: {String(error).slice(0, 200)}
      </div>
    );
  }
  if (!data) {
    return (
      <div style={{ padding: 24, color: "#94a3b8", display: "flex",
                      alignItems: "center", gap: 10 }}
          data-testid="dashboard-loading">
        <Loader2 size={14}
            style={{ animation: "spin 1s linear infinite" }} />
        Loading project dashboard…
      </div>
    );
  }

  const cur = data.cost?.currency === "INR" ? "₹" : "$";
  const formatINR = (n) => {
    if (n == null) return "—";
    if (n >= 1e7) return `${cur}${(n / 1e7).toFixed(2)} Cr`;
    if (n >= 1e5) return `${cur}${(n / 1e5).toFixed(2)} L`;
    return `${cur}${n.toLocaleString()}`;
  };
  const sortedRisks = (data.risks || []).slice().sort((a, b) => {
    const order = { high: 0, medium: 1, low: 2 };
    return (order[a.severity] ?? 9) - (order[b.severity] ?? 9);
  });

  return (
    <div style={{ background: "#020617", minHeight: "100vh",
                    color: "#e2e8f0", padding: "24px 32px" }}
        data-testid="project-dashboard">
      {/* Top bar */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12,
        marginBottom: 24,
      }}>
        <Link to="/projects"
            data-testid="dashboard-back-link"
            style={{
              color: "#94a3b8", display: "inline-flex",
              alignItems: "center", gap: 6, fontSize: 13,
              textDecoration: "none",
            }}>
          <ArrowLeft size={14}/> Projects
        </Link>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}
              data-testid="dashboard-project-name">
            {data.project_name || "Project"}
          </h1>
          <div style={{ fontSize: 12, color: "#94a3b8",
                          marginTop: 4 }}>
            {data.industry} · {data.country} ·{" "}
            {data.stages_done}/{data.stages_total} stages run
            ({data.stages_done_pct}%) ·{" "}
            {data.deliverables_ready}/{data.deliverables_total}{" "}
            deliverables ready
          </div>
        </div>
        <span data-testid="dashboard-overall-status"
            style={{
              padding: "6px 14px", borderRadius: 6, fontSize: 12,
              fontWeight: 700,
              color: data.compliance.overall === "PASS"
                ? "#10b981" : "#ef4444",
              background: data.compliance.overall === "PASS"
                ? "#10b98122" : "#ef444422",
            }}>
          {data.compliance.overall === "PASS"
            ? <><CheckCircle2 size={12}
                  style={{ verticalAlign: -1 }}/> COMPLIANT</>
            : <><XCircle size={12}
                  style={{ verticalAlign: -1 }}/> REVIEW</>}
        </span>
      </div>

      {/* Quick links */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20,
                      flexWrap: "wrap" }}>
        <Link
          to={`/projects/${projectId}`}
          data-testid="dashboard-link-workspace"
          style={{ display: "inline-flex", alignItems: "center",
                    gap: 6, fontSize: 12, color: "#cbd5e1",
                    padding: "6px 12px",
                    background: "#0f172a",
                    border: "1px solid #334155",
                    borderRadius: 4, textDecoration: "none" }}>
          <Cog size={12}/> Workspace
        </Link>
        <Link
          to={`/projects/${projectId}/master-plan`}
          data-testid="dashboard-link-masterplan"
          style={{ display: "inline-flex", alignItems: "center",
                    gap: 6, fontSize: 12, color: "#cbd5e1",
                    padding: "6px 12px",
                    background: "#0f172a",
                    border: "1px solid #334155",
                    borderRadius: 4, textDecoration: "none" }}>
          <Map size={12}/> Master Plan
        </Link>
        <Link
          to={`/projects/${projectId}/engineering`}
          data-testid="dashboard-link-engineering"
          style={{ display: "inline-flex", alignItems: "center",
                    gap: 6, fontSize: 12, color: "#cbd5e1",
                    padding: "6px 12px",
                    background: "#0f172a",
                    border: "1px solid #334155",
                    borderRadius: 4, textDecoration: "none" }}>
          <Layers size={12}/> Engineering
        </Link>
        {/* RG9: Review Existing Design — visible for real_estate projects.
             Dashboard API returns `industry` (user-facing, `generic` on
             legacy records); the canonical flag lives on the project doc
             as `domain`, so we check both — companion fetch populates
             projMeta. */}
        {(projMeta?.domain === "real_estate" ||
          data?.industry === "real_estate" ||
          data?.industry === "Real Estate" ||
          data?.industry === "Real-Estate") && (
          <Link
            to={`/projects/${projectId}?tab=design-review`}
            data-testid="dashboard-link-design-review"
            style={{ display: "inline-flex", alignItems: "center",
                      gap: 6, fontSize: 12, color: "#020617",
                      padding: "6px 12px",
                      background: "#22D3EE",
                      border: "1px solid #22D3EE",
                      borderRadius: 4, textDecoration: "none",
                      fontWeight: 600 }}>
            <Layers size={12}/> Review Existing Design
          </Link>
        )}
        <a href={`${API}/api/projects/${projectId}/documents/`
                  + `procurement-pack.xlsx`}
            data-testid="dashboard-link-procurement"
            style={{ display: "inline-flex", alignItems: "center",
                      gap: 6, fontSize: 12, color: "#cbd5e1",
                      padding: "6px 12px",
                      background: "#7c3aed",
                      border: "1px solid #6d28d9",
                      borderRadius: 4, textDecoration: "none",
                      fontWeight: 600 }}>
          <FileSpreadsheet size={12}/> Procurement Pack
        </a>
        <a href={`${API}/api/projects/${projectId}/documents/`
                  + `all-drawings.zip`}
            data-testid="dashboard-link-cad-export"
            style={{ display: "inline-flex", alignItems: "center",
                      gap: 6, fontSize: 12, color: "#cbd5e1",
                      padding: "6px 12px",
                      background: "#0891b2",
                      border: "1px solid #0e7490",
                      borderRadius: 4, textDecoration: "none",
                      fontWeight: 600 }}>
          <Download size={12}/> All Drawings (DXF ZIP)
        </a>
        <a href={`${API}/api/projects/${projectId}/documents/`
                  + `ifc-drawing-set.zip`}
            data-testid="dashboard-link-ifc"
            style={{ display: "inline-flex", alignItems: "center",
                      gap: 6, fontSize: 12, color: "#cbd5e1",
                      padding: "6px 12px",
                      background: "#059669",
                      border: "1px solid #047857",
                      borderRadius: 4, textDecoration: "none",
                      fontWeight: 600 }}>
          <Download size={12}/> IFC Set (BIM ZIP)
        </a>
        <a href={`${API}/api/projects/${projectId}/documents/`
                  + `compliance-pack.zip`}
            data-testid="dashboard-link-compliance-pack"
            title="Bundles area-statement + structural-basis + fire-report + MEP-report + dashboard JSON into one ZIP. Add ?ai=1 to the URL for AI-narrative variants (slower, premium output)."
            style={{ display: "inline-flex", alignItems: "center",
                      gap: 6, fontSize: 12, color: "#fef3c7",
                      padding: "6px 12px",
                      background: "#b45309",
                      border: "1px solid #92400e",
                      borderRadius: 4, textDecoration: "none",
                      fontWeight: 600 }}>
          <FileCheck2 size={12}/> Compliance Pack
        </a>
        <Link
          to={`/projects/${projectId}/comparison`}
          data-testid="dashboard-link-compare"
          style={{ display: "inline-flex", alignItems: "center",
                    gap: 6, fontSize: 12, color: "#cbd5e1",
                    padding: "6px 12px",
                    background: "#0f172a",
                    border: "1px solid #334155",
                    borderRadius: 4, textDecoration: "none" }}>
          <GitCompare size={12}/> Compare revisions
        </Link>
      </div>

      {/* KPI row */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap",
                      marginBottom: 16 }}>
        <KpiTile testid="kpi-bua" label="Total BUA"
            value={`${(data.totals.bua_sqm || 0)
                          .toLocaleString()} m²`}
            sub={`${data.totals.n_buildings} buildings, `
              + `up to ${data.totals.floors_max} floors`} />
        <KpiTile testid="kpi-fsi" label="FSI"
            value={data.compliance.fsi.actual}
            sub={data.compliance.fsi.limit
              ? `limit ${data.compliance.fsi.limit} — `
                + data.compliance.fsi.status
              : "no limit set"}
            status={data.compliance.fsi.status} />
        <KpiTile testid="kpi-coverage" label="Coverage"
            value={`${data.compliance.coverage.actual_pct}%`}
            sub={data.compliance.coverage.limit_pct
              ? `limit ${data.compliance.coverage.limit_pct}% — `
                + data.compliance.coverage.status
              : "no limit set"}
            status={data.compliance.coverage.status} />
        <KpiTile testid="kpi-cost" label="Indicative cost"
            value={formatINR(data.cost?.grand_total)}
            sub={data.cost?.cost_per_sqm_bua
              ? `${formatINR(data.cost.cost_per_sqm_bua)}/m² BUA`
              : null} />
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)",
        gap: 16,
      }}>
        {/* Left column — stages + cost */}
        <div style={{ display: "flex", flexDirection: "column",
                        gap: 16 }}>
          <Card data-testid="dashboard-stages">
            <h3 style={{ margin: 0, marginBottom: 12, fontSize: 14,
                            color: "#cbd5e1" }}>
              <Activity size={14}
                  style={{ verticalAlign: -2, marginRight: 6 }}/>
              Stage completion
            </h3>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
              gap: 8,
            }}>
              {data.progress.map((row) => (
                <StageTile key={row.stage} row={row} />
              ))}
            </div>
          </Card>

          <Card data-testid="dashboard-cost">
            <h3 style={{ margin: 0, marginBottom: 12, fontSize: 14,
                            color: "#cbd5e1" }}>
              <FileSpreadsheet size={14}
                  style={{ verticalAlign: -2, marginRight: 6 }}/>
              Cost breakdown ({data.cost?.currency || "INR"})
            </h3>
            <div style={{ display: "flex", flexDirection: "column",
                            gap: 8 }}>
              {Object.entries(data.cost?.sections || {})
                .map(([sec, amount]) => {
                  const grand = data.cost.grand_total || 1;
                  const pct = (100 * amount / grand);
                  return (
                    <div key={sec}
                        data-testid={`dashboard-cost-row-${
                          sec.toLowerCase().replace(/\s+/g, "-")
                        }`}>
                      <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 12, marginBottom: 4,
                      }}>
                        <span style={{ color: "#cbd5e1" }}>{sec}</span>
                        <span style={{ color: "#94a3b8" }}>
                          {formatINR(amount)} · {pct.toFixed(1)}%
                        </span>
                      </div>
                      <div style={{
                        height: 6, background: "#0f172a",
                        borderRadius: 3, overflow: "hidden",
                      }}>
                        <div style={{
                          width: `${Math.max(pct, 0.5)}%`,
                          height: "100%",
                          background: "linear-gradient("
                            + "90deg,#7c3aed,#a78bfa)",
                        }}/>
                      </div>
                    </div>
                  );
                })}
            </div>
          </Card>
        </div>

        {/* Right column — issues + revisions */}
        <div style={{ display: "flex", flexDirection: "column",
                        gap: 16 }}>
          <Card data-testid="dashboard-risks">
            <h3 style={{ margin: 0, marginBottom: 12, fontSize: 14,
                            color: "#cbd5e1" }}>
              <AlertCircle size={14}
                  style={{ verticalAlign: -2, marginRight: 6 }}/>
              Issues & risks ({sortedRisks.length})
            </h3>
          {sortedRisks.length === 0 ? (
            <div style={{
              color: "#10b981", fontSize: 12, padding: 24,
              textAlign: "center",
            }}>
              <CheckCircle2 size={28}
                  style={{ marginBottom: 6, opacity: 0.6 }}/>
              <div>No issues detected.</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column",
                            gap: 8 }}>
              {sortedRisks.map((r, i) => {
                const sc = SEV_COLOR[r.severity] || SEV_COLOR.low;
                return (
                  <div key={i}
                      data-testid={`dashboard-risk-${r.code}`}
                      style={{
                        padding: 10,
                        background: sc.bg,
                        border: `1px solid ${sc.border}`,
                        borderRadius: 6,
                      }}>
                    <div style={{ display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    marginBottom: 4 }}>
                      <span style={{ fontWeight: 700, fontSize: 12,
                                        color: sc.color }}>
                        {r.title}
                      </span>
                      <span style={{
                        fontSize: 9, fontWeight: 700,
                        padding: "1px 6px",
                        background: sc.border, color: "#020617",
                        borderRadius: 3,
                        textTransform: "uppercase",
                      }}>{r.severity}</span>
                    </div>
                    <div style={{ fontSize: 11, color: "#94a3b8" }}>
                      {r.detail}
                    </div>
                    {r.stage && (
                      <Link
                        to={`/projects/${projectId}/engineering`}
                        data-testid={`dashboard-risk-${r.code}-link`}
                        style={{
                          fontSize: 10, color: sc.color, marginTop: 4,
                          display: "inline-block",
                          textDecoration: "underline",
                        }}>
                        Open Stage {r.stage} →
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
          <RevisionPanel projectId={projectId} />
        </div>
      </div>
    </div>
  );
}
